import React, {Component} from "react";
import PropTypes from "prop-types";
import "./_video-player.scss";
import Button from "../button/Button";
import {connect} from "react-redux";
import {setVideoFoPiP} from "../../../actions/pipActions";
import VideoPlayerProgress from "./VideoPlayerProgress";
import {getVideoForPiP} from "../../../selectors/pipSelector";
import {isMobile} from "../../../utils/MobileUtils";
import {isEqual} from "lodash";
import LoadingComponent from "../../loading/LoadingComponent";
import {compose} from "redux";
import {withTranslation} from "react-i18next";
import Hls from "hls.js";
import {catchify} from "../../../utils/Utils";
import {Milliseconds} from "../../../constans/milliseconds";
import {enterFullscreen, exitFullscreen} from "../../../utils/DOMUtils";
import {HLS_USER_VISIBLE_ERROR_CODES} from "../../../utils/CameraUtils";
import {camelOrPascalCaseToScreamingSnakeCase} from "../../../utils/TextUtils";
import {Browser} from "../browser-detection/Browser";

function mapStateToProps(state) {
    return {
        video: getVideoForPiP(state),
        locale: state.language.locale,
    };
}

class VideoPlayer extends Component {
    state = {
        playing: this.props.autoPlay,
        replay: false,
        inFullscreen: false,
        inPiP: false,
        liveError: null,
    };
    video = React.createRef();
    container = React.createRef();
    duration = React.createRef();
    hls = null;
    liveStallTimeout = null;
    isSafari = Browser.is.Safari(true);

    componentDidMount() {
        this.video.current.addEventListener("play", this.onVideoPlay);
        this.video.current.addEventListener("pause", this.onVideoPause);
        this.video.current.addEventListener("ended", this.onVideoEnd);
        this.video.current.addEventListener("timeupdate", this.onTimeUpdate);
        this.video.current.addEventListener(
            "durationchange",
            this.onVideoDurationChanged
        );
        document.addEventListener("fullscreenchange", this.onFullScreenChange);
        this.setDuration();
        this.onSourcesChanged(true);
    }

    onSourcesChanged = (initialRender) => {
        const {sources, videoType} = this.props;
        clearTimeout(this.liveStallTimeout);

        // Check if it's an HLS stream
        const isHLSStream =
            sources.length === 1 &&
            sources[0].src?.includes("amazonaws.com/hls/v1/");

        // Cleanup previous Hls instance if any
        if (this.hls) {
            this.hls.destroy();
            this.hls = null;
        }
        // Handle case where no HLS source is yet available
        if (sources.length === 1 && !sources[0].src && videoType === "live") {
            this.setState({liveError: "NOT_AVAILABLE"});
        }

        const videoEl = this.video.current;

        // Native HLS for Safari
        if (isHLSStream) {
            videoEl.loop = false;
            if (
                this.isSafari &&
                videoEl.canPlayType("application/vnd.apple.mpegurl")
            ) {
                videoEl.src = sources[0].src;
                videoEl.addEventListener("canplay", this.handleSafariCanPlay);
                videoEl.addEventListener("error", this.handleSafariError);
                videoEl.load();
            }
            // Fallback to Hls.js for non-Safari or unsupported browsers
            else if (Hls.isSupported()) {
                this.hls = new Hls({
                    liveDurationInfinity: videoType === "live",
                });
                this.hls.loadSource(sources[0].src);
                this.hls.attachMedia(videoEl);

                // Auto-play after manifest is parsed
                this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    videoEl.play();
                });

                // Monitor for live errors and stalls
                if (videoType === "live") {
                    this.setState({liveError: null});

                    this.hls.on(Hls.Events.FRAG_LOADED, () => {
                        if (this.state.liveError) {
                            this.setState({liveError: null});
                        }
                        clearTimeout(this.liveStallTimeout);
                        this.liveStallTimeout = setTimeout(() => {
                            this.setState({liveError: "STREAM_STALLED"});
                        }, 15 * Milliseconds.SECOND);
                    });

                    this.hls.on(Hls.Events.ERROR, (event, data) => {
                        if (data.fatal) {
                            const visible =
                                HLS_USER_VISIBLE_ERROR_CODES.includes(
                                    data.details
                                );
                            this.setState({
                                liveError: visible
                                    ? camelOrPascalCaseToScreamingSnakeCase(
                                          data.details
                                      )
                                    : "FATAL_ERROR",
                            });
                        }
                    });
                }
            }
        } else if (!initialRender) {
            let shouldAutoPlay =
                videoType === "live" &&
                sources?.length === 1 &&
                sources?.[0]?.src;
            if (shouldAutoPlay) {
                videoEl.loop = true;
                videoEl.src = sources[0].src;
                videoEl
                    .play()
                    .then(() => {
                        this.setState({liveError: null});
                    })
                    .catch(console.warn);
            } else {
                videoEl?.load();
            }
            this.setState({replay: false, playing: shouldAutoPlay});
        }
    };

    componentDidUpdate(prevProps, prevState) {
        if (this.props.video === null && prevProps.video !== null) {
            this.setState({
                inPiP: false,
            });
        }
        if (!isEqual(this.props.sources, prevProps.sources)) {
            this.onSourcesChanged(false);
        }
        // if (!isEqual(this.props.subtitles, prevProps.subtitles)) {
        //     let tracks = this.video.current?.querySelectorAll("track");
        //     console.log(tracks);
        //     for (let track of tracks) {
        //         this.video.current.removeChild(track);
        //     }
        // }
    }

    handleSafariCanPlay = () => {
        this.video.current.play().catch((err) => {
            console.warn("Safari autoplay error:", err);
            this.setState({liveError: "FATAL_ERROR"});
        });
    };

    handleSafariError = () => {
        console.error("Safari video playback error");
        this.setState({liveError: "FATAL_ERROR"});
    };

    componentWillUnmount() {
        if (this.hls !== null) {
            catchify(() => {
                this.hls.destroy();
                this.hls = null;
                this.video.current.pause();
                this.video.current.src = "";
                this.video.current.load();
            })();
        }

        this.video.current.removeEventListener("play", this.onVideoPlay);
        this.video.current.removeEventListener("pause", this.onVideoPause);
        this.video.current.removeEventListener("ended", this.onVideoEnd);
        this.video.current.removeEventListener("timeupdate", this.onTimeUpdate);
        this.video.current.removeEventListener(
            "durationchange",
            this.onVideoDurationChanged
        );
        document.removeEventListener(
            "fullscreenchange",
            this.onFullScreenChange
        );
        this.video.current.removeEventListener("error", this.handleSafariError);
        this.video.current.removeEventListener(
            "canplay",
            this.handleSafariCanPlay
        );
    }

    onVideoPlay = () => {
        const {onVideoPlay} = this.props;
        this.setState({
            playing: true,
            replay: false,
        });
        onVideoPlay && onVideoPlay();
    };

    onVideoPause = () => {
        const {onVideoPause} = this.props;
        this.setState({
            playing: false,
        });
        onVideoPause && onVideoPause();
    };

    onVideoEnd = () => {
        this.setState({
            replay: true,
        });
        const progressBar = this.getProgressBar();
        if (progressBar) {
            progressBar.style.width = "100%";
        }
    };

    onVideoDurationChanged = () => {
        this.setDuration();
        // commenting this fixed the progressbar jumping back and forth when user is watching a LIVE stream
        // let progress = this.container.current.querySelector(".progress-bar");
        // progress.style.width = "0";
    };

    getProgressBar() {
        const {videoType} = this.props;
        if (videoType === "live") {
            return null;
        }
        return this.container.current.querySelector(".progress-bar");
    }

    onTimeUpdate = () => {
        let progress = Math.floor(
            (100 / this.video.current.duration) * this.video.current.currentTime
        );
        const progressBar = this.getProgressBar();
        if (progressBar) {
            progressBar.style.width = progress + "%";
        }
        this.setDuration();
    };

    onFullScreenChange = () => {
        this.setState({
            inFullscreen: !!document.fullscreenElement,
        });
    };

    onPlayClick = () => {
        const {videoType} = this.props;
        if (videoType === "live" && this.hls?.liveSyncPosition) {
            this.video.current.currentTime = this.hls.liveSyncPosition;
        }
        this.video.current.play();
    };

    onPauseClick = () => {
        this.video.current.pause();
    };

    onFullscreenClick = async () => {
        try {
            await enterFullscreen(this.container.current, this.video.current);
        } catch (e) {
            console.error(e);
        }
    };

    onCompressClick = async () => {
        try {
            await exitFullscreen();
        } catch (e) {
            console.error(e);
        }
    };

    getPlayIcon() {
        const {playing, replay} = this.state;
        if (replay) return <i className="fas fa-redo" />;
        if (playing) return <i className="fas fa-pause" />;
        return <i className="fas fa-play" />;
    }

    getClassName() {
        const {inFullscreen} = this.state;
        let className = "fetura-video-player";
        if (inFullscreen) className += " fullscreen";
        return className;
    }

    getFullscreenIcon() {
        const {inFullscreen} = this.state;
        if (inFullscreen) return <i className="fas fa-compress" />;
        return <i className="fas fa-expand" />;
    }

    onPiPClick = () => {
        const {sources, subtitles, onVideoPause, onVideoPlay, videoType} =
            this.props;
        const {playing} = this.state;
        this.onPauseClick();
        this.props.dispatch(
            setVideoFoPiP({
                sources,
                playOnStart: playing,
                videoElement: this.video.current,
                subtitles,
                onVideoPause,
                onVideoPlay,
                videoType,
            })
        );
        this.setState({
            inPiP: true,
        });
    };

    setVideoTime(time) {
        this.video.current.currentTime = time;
    }

    onProgressClick = (e) => {
        let offsetX = e.nativeEvent.offsetX;
        let offsetWidth = e.target.offsetWidth;
        let target = e.target;
        if (!target.classList.contains("progress")) {
            offsetWidth = target.parentElement.offsetWidth;
        }
        let percent = offsetX / offsetWidth;
        this.video.current.currentTime = percent * this.video.current.duration;
        if (target.classList.contains("progress")) {
            target = this.getProgressBar();
        }
        target.style.width = Math.round(percent * 100) + "%";
        if (this.state.replay) {
            this.setState({
                replay: false,
            });
        }
    };

    formatTime(time) {
        if (isNaN(time)) return "--:--";
        let seconds = Math.floor(time) % 60;
        let minutes = Math.floor(time / 60);
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    setDuration() {
        const {videoType, t} = this.props;
        if (videoType === "live") {
            this.duration.current.innerHTML = t("videoPlayerStream.LIVE");
        } else {
            this.duration.current.innerHTML = `${this.formatTime(this.video.current.currentTime)} / ${this.formatTime(this.video.current.duration)}`;
        }
    }

    getDefaultTrack() {
        const {subtitles, locale} = this.props;
        let track = subtitles.find((item) => item.language === locale);
        if (track) return track;
        let englishTrack = subtitles.find((item) => item.language === "en");
        if (englishTrack) return englishTrack;
        return subtitles[0];
    }

    render() {
        const {
            sources,
            autoPlay,
            showPiP,
            customButtons,
            loading,
            t,
            showFullScreen,
            videoType,
        } = this.props;
        const {playing, inFullscreen, inPiP, liveError} = this.state;
        let defaultTrack = this.getDefaultTrack();
        const isOnDemandLoading = videoType === "recording" ? loading : false;
        const isLiveLoading =
            videoType === "live"
                ? [null, "NOT_AVAILABLE"].includes(liveError) && loading
                : false;
        return (
            <div className={this.getClassName()} ref={this.container}>
                <LoadingComponent isLoading={isOnDemandLoading} />
                <video
                    autoPlay={autoPlay}
                    ref={this.video}
                    onClick={
                        inPiP
                            ? playing
                                ? this.onPauseClick
                                : this.onPlayClick
                            : null
                    }
                    playsInline={true}
                    crossOrigin="anonymous">
                    {sources.map((source, index) => (
                        <source
                            src={source.src}
                            type={source.type}
                            key={source.src}
                        />
                    ))}
                    {defaultTrack && (
                        <track
                            default
                            kind="captions"
                            srcLang={defaultTrack.language}
                            src={defaultTrack.url}
                            key={defaultTrack.url}
                        />
                    )}
                </video>
                <div className="fetura-video-player-controls">
                    {videoType === "recording" && (
                        <div className="fetura-video-player-controls-progress">
                            <VideoPlayerProgress
                                videoType={videoType}
                                onProgressClick={this.onProgressClick}
                            />
                        </div>
                    )}
                    <div className="fetura-video-player-controls-buttons">
                        <div className="fetura-video-player-controls-buttons-first">
                            <Button
                                buttonStyle={"text"}
                                icon={this.getPlayIcon()}
                                buttonColor={"white"}
                                onClick={
                                    playing
                                        ? this.onPauseClick
                                        : this.onPlayClick
                                }
                            />
                            <div
                                className="fetura-video-player-controls-buttons-first-duration"
                                ref={this.duration}
                            />
                        </div>
                        <div className="fetura-video-player-controls-buttons-last">
                            {showPiP && !isMobile() && (
                                <Button
                                    buttonStyle={"text"}
                                    icon={
                                        <i className="far fa-rectangle-landscape" />
                                    }
                                    buttonColor={"white"}
                                    onClick={this.onPiPClick}
                                />
                            )}
                            {customButtons}
                            {showFullScreen && (
                                <Button
                                    buttonStyle={"text"}
                                    icon={this.getFullscreenIcon()}
                                    buttonColor={"white"}
                                    onClick={
                                        inFullscreen
                                            ? this.onCompressClick
                                            : this.onFullscreenClick
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
                {inPiP && (
                    <div className="fetura-video-player-pip">
                        {t("pipRunning")}
                    </div>
                )}
                {videoType === "live" && (
                    <>
                        {isLiveLoading ? (
                            <div className="fetura-video-player-pip">
                                <i
                                    className={
                                        "fas fa-circle-notch fa-2x fa-spin"
                                    }
                                />
                            </div>
                        ) : liveError !== null ? (
                            <div className="fetura-video-player-pip">
                                {t(`videoPlayerError.${liveError}`)}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    }
}

VideoPlayer.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.node.isRequired,
            type: PropTypes.string,
        })
    ).isRequired,
    subtitles: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            language: PropTypes.string,
        })
    ),
    autoPlay: PropTypes.bool,
    showPiP: PropTypes.bool,
    customButtons: PropTypes.arrayOf(PropTypes.node),
    loading: PropTypes.bool,
    showFullScreen: PropTypes.bool,
    onVideoPlay: PropTypes.func,
    onVideoPause: PropTypes.func,
    videoType: PropTypes.oneOf(["live", "recording"]),
};

VideoPlayer.defaultProps = {
    autoPlay: true,
    showPiP: true,
    customButtons: [],
    subtitles: [],
    loading: false,
    showFullScreen: true,
    videoType: "recording",
};

export default compose(
    withTranslation(null, {withRef: true}),
    connect(mapStateToProps, null, null, {forwardRef: true})
)(VideoPlayer);
